"use client";

import { useCallback, useEffect, useRef } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";

export interface UseHistoryModalOptions {
  /** Stable identifier for this dialog, written to the URL. */
  modalId: string;
  /**
   * Query parameter that carries the modal id.
   * @default "dialog"
   */
  paramName?: string;
  /**
   * Extra query params that identify THIS instance of the dialog — e.g.
   * `{ item: file.id }` when the same dialog exists once per row. They are
   * written on open, removed on close, and must all match for the dialog to
   * read as open (so two rows never open each other's dialog).
   */
  params?: Record<string, string | undefined>;
}

export interface UseHistoryModalReturn {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  /** Drop-in for Radix/shadcn `onOpenChange`. */
  onOpenChange: (open: boolean) => void;
  /** Shareable URL that opens this dialog. */
  getUrl: () => string;
}

/**
 * A dialog whose open/closed state lives in the URL, so the browser Back
 * button closes it and Forward re-opens it.
 *
 * Opening pushes a history entry; closing unwinds that entry with
 * `router.back()` rather than pushing another one, so a user who opens and
 * closes a dialog five times does not have to press Back ten times to leave
 * the page. Pressing Back while the dialog is open simply drops the param and
 * the derived `isOpen` goes false — no listener required.
 *
 * Use this for dialogs the user perceives as a place (a preview, a share
 * sheet, a mobile nav drawer). Do NOT use it for transient confirmations or
 * menus — a Forward that restores a confirm prompt is worse than no history.
 *
 * @example
 * ```tsx
 * const preview = useHistoryModal({ modalId: "drive-preview", params: { item: file.id } });
 * <Dialog open={preview.isOpen} onOpenChange={preview.onOpenChange}>…</Dialog>
 * <button onClick={preview.open}>Preview</button>
 * ```
 */
export function useHistoryModal({
  modalId,
  paramName = "dialog",
  params,
}: UseHistoryModalOptions): UseHistoryModalReturn {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const matches =
    searchParams?.get(paramName) === modalId &&
    Object.entries(params ?? {}).every(
      ([key, value]) => value === undefined || searchParams?.get(key) === value
    );
  const isOpen = Boolean(matches);

  // Whether WE created the history entry currently showing this dialog. False
  // after a Forward/deep-link arrival, where the entry already existed.
  const pushedRef = useRef(false);
  useEffect(() => {
    if (!isOpen) pushedRef.current = false;
  }, [isOpen]);

  const basePath = pathname ?? "";

  const buildOpenUrl = useCallback(() => {
    const next = new URLSearchParams(searchParams?.toString() ?? "");
    next.set(paramName, modalId);
    for (const [key, value] of Object.entries(params ?? {})) {
      if (value !== undefined) next.set(key, value);
    }
    return `${basePath}?${next.toString()}`;
  }, [basePath, searchParams, paramName, modalId, params]);

  const open = useCallback(() => {
    if (isOpen) return;
    pushedRef.current = true;
    router.push(buildOpenUrl());
  }, [isOpen, router, buildOpenUrl]);

  const close = useCallback(() => {
    if (!isOpen) return;
    if (pushedRef.current) {
      pushedRef.current = false;
      router.back();
      return;
    }
    const next = new URLSearchParams(searchParams?.toString() ?? "");
    next.delete(paramName);
    for (const key of Object.keys(params ?? {})) next.delete(key);
    const query = next.toString();
    router.replace(`${basePath}${query ? `?${query}` : ""}`);
  }, [isOpen, router, searchParams, paramName, params, basePath]);

  const onOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (nextOpen) open();
      else close();
    },
    [open, close]
  );

  return { isOpen, open, close, onOpenChange, getUrl: buildOpenUrl };
}
